import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import CheckoutSummary from './subcomponents/CheckoutSummary';
import CustomerDetails from './subcomponents/CustomerDetails';
import FamilyDiscountDetails from './subcomponents/FamilyDiscountDetails';
import NewOrder from '../cart/NewOrder';
import KeepShoppingButton from '../cart/KeepShoppingButton';
import DiscountCode from './subcomponents/DiscountCode';
import cartUtils from '../../../utils/cart';
import {
  cancelRetouchingCheckout,
  resetSubmittingOrder,
  storeCart,
  submitCart,
} from '../../../actions/v3Order';

// Adds padding to account for missing banner message component
const CheckoutWrapper = styled.div`
  padding-top: 24px;
`;

const MainHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const Divider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const CheckoutRight = styled.div`
  float: right;
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Checkout = ({ history }) => {
  const dispatch = useDispatch();
  const {
    cartTotals,
    discountCodes,
    storingCart,
    submittingOrder,
    orders,
    retouchingQuoteGeneration,
    retouchingQuoteError,
    retouchingCheckoutProtected,
    retouchingCheckoutRequest,
    retouchingCheckoutMessage,
  } = useSelector((state) => state.v3Order);
  const company = useSelector((state) => state.company);
  const location = useLocation();
  const processedCancel = useRef(null);
  const stripeCancelUrl = window.location.href;
  const query = new URLSearchParams(location.search);
  const attemptId = query.get('retouchingAttemptId');
  const cancelUuid = query.get('cart_uuid');

  const onlyRetouching = cartUtils.anyOrderContainsOnlyRetouching(orders);

  useEffect(() => {
    // Check if any order contains only retouching products
    if (onlyRetouching) {
      // Redirect back to cart with a message
      history.push('/v3/cart');
      return;
    }

    dispatch(resetSubmittingOrder());
    if (attemptId) {
      const returnKey = `${cancelUuid}:${attemptId}`;
      if (processedCancel.current !== returnKey) {
        processedCancel.current = returnKey;
        dispatch(cancelRetouchingCheckout(cancelUuid, attemptId)).then(
          (reopened) => {
            if (reopened) {
              if (history.replace) history.replace('/v3/checkout');
              else dispatch(storeCart());
            }
          },
        );
      }
      return;
    }
    dispatch(storeCart());
  }, [
    retouchingQuoteGeneration,
    onlyRetouching,
    attemptId,
    cancelUuid,
    history,
    dispatch,
  ]);

  const handleSubmit = (addedPicsPlus = false, offersPicsPlus = false) => {
    let customerAddedPicsPlus = false;

    if (offersPicsPlus === true && addedPicsPlus === true) {
      customerAddedPicsPlus = true;
    }

    dispatch(submitCart(stripeCancelUrl, customerAddedPicsPlus));
  };

  const getLoadingSpinnerClass = () => {
    if (storingCart || submittingOrder) {
      return 'loading';
    }

    return 'hidden';
  };

  return (
    <CheckoutWrapper>
      <div className={getLoadingSpinnerClass()}>
        <div className="spinner" />
      </div>
      {(retouchingQuoteError ||
        retouchingCheckoutProtected ||
        retouchingCheckoutRequest ||
        retouchingCheckoutMessage) && (
        <p role="alert">
          {retouchingQuoteError ||
            retouchingCheckoutMessage ||
            'Your checkout is protected. Please retry the original payment, cancel from its return page, or contact support.'}
        </p>
      )}
      <MainHeader>Customer Details &amp; Payment</MainHeader>
      <Divider />
      <div className="order-checkout__main-content row">
        <div className="order-checkout__left col-xs-12 col-md-8 col-lg-8">
          <CustomerDetails cartTotals={cartTotals} />
          <FamilyDiscountDetails cartTotals={cartTotals} />
        </div>
        <CheckoutRight className="col-xs-12 col-md-4 col-lg-4">
          <CheckoutSummary
            cartTotals={cartTotals}
            discountCodes={discountCodes}
            handleSubmit={handleSubmit}
            submittingOrder={submittingOrder}
          />
          <DiscountCode />
          <NewOrder company={company} />
          <KeepShoppingButton />
        </CheckoutRight>
      </div>
    </CheckoutWrapper>
  );
};

export default Checkout;
